import React from 'react';

import NextLink from 'next/link';

import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';

import { isLLM } from '@/lib/utils';

/** Wraps Next.js links for consistent MUI `component` integration. */
const LinkBehavior = React.forwardRef<HTMLAnchorElement, React.ComponentProps<typeof NextLink>>(
  function LinkBehavior(props, ref) {
    return <NextLink ref={ref} {...props} />;
  }
);

interface UserLinkProps {
  /** Identifies the user whose profile path is used for linking. */
  username: string;
  /** Overrides the rendered text or content inside the link wrapper. */
  children?: React.ReactNode;
  /** Forces non-link text output to avoid nested interactivity. */
  disabled?: boolean;
  /** Controls the MUI underline mode for linked output. */
  underline?: 'none' | 'hover' | 'always';
}

/** Renders a profile link for humans and plain text for AI or disabled contexts. */
export const UserLink: React.FC<UserLinkProps> = ({
  username,
  children,
  disabled = false,
  underline = 'none',
}) =>
  disabled || isLLM(username) ? (
    <Typography
      component="span"
      variant="inherit"
      aria-label={isLLM(username) ? `AI model ${username}` : undefined}
    >
      {children ?? username}
    </Typography>
  ) : (
    <Link
      component={LinkBehavior}
      href={`/citizen/${encodeURIComponent(username)}`}
      aria-label={`View profile of ${username}`}
      underline={underline}
      variant="inherit"
    >
      {children ?? username}
    </Link>
  );
